import type { BudgetListItem, SettlementHistoryItem, SettlementListItem } from '@fdh/shared';

export type EmployerMoneyGivenBadgeKind = 'unsettled' | 'pending_review' | 'settled';

export type EmployerMoneyGivenCardView = {
  badge: EmployerMoneyGivenBadgeKind | null;
  activeSettlement: SettlementListItem | null;
  detailPath: string;
  showSettlementResult: boolean;
};

const isActiveSettlementStatus = (status: SettlementListItem['status']): boolean =>
  status === 'submitted' || status === 'reviewed';

/** Match settlement to a main Money Given row (budget_id first, then legacy exact dates). */
export const settlementBelongsToBudget = (
  settlement: SettlementListItem,
  budget: BudgetListItem
): boolean => {
  if (settlement.budgetId) {
    return settlement.budgetId === budget.id;
  }
  return settlement.startDate === budget.startDate && settlement.endDate === budget.endDate;
};

export const resolveSettlementsForBudget = (
  budget: BudgetListItem,
  settlements: SettlementListItem[]
): SettlementListItem[] =>
  settlements.filter((row) => settlementBelongsToBudget(row, budget));

export const resolveActiveSettlementForBudget = (
  budget: BudgetListItem,
  settlements: SettlementListItem[]
): SettlementListItem | null => {
  const matched = resolveSettlementsForBudget(budget, settlements).filter((row) =>
    isActiveSettlementStatus(row.status)
  );
  if (matched.length === 0) {
    return null;
  }
  return matched.sort((a, b) => b.startDate.localeCompare(a.startDate))[0] ?? null;
};

/** Helper may submit settlement when period has no submitted/reviewed (pending or locked) record. */
export const isHelperBudgetPeriodSettleable = (
  budget: BudgetListItem,
  settlements: SettlementListItem[]
): boolean => resolveActiveSettlementForBudget(budget, settlements) === null;

export const resolveSettleableHelperMainBudgets = (
  mainBudgets: BudgetListItem[],
  settlements: SettlementListItem[]
): BudgetListItem[] =>
  mainBudgets.filter((row) => isHelperBudgetPeriodSettleable(row, settlements));

/** Badge label on helper settlement history list (By settlement tab ribbon). */
export const resolveSettlementHistoryBadgeLabel = (
  status: SettlementListItem['status'],
  locked: boolean,
  t: (key: string) => string
): string => {
  if (status === 'submitted') return t('settlement.status.submitted');
  if (status === 'reviewed' || locked) return t('settlement.locked');
  if (status === 'withdrawn') return t('settlement.status.withdrawn');
  if (status === 'rejected') return t('settlement.status.rejected');
  return t('settlement.unlocked');
};

export const resolveEmployerMoneyGivenCardView = (
  budget: BudgetListItem,
  settlements: SettlementListItem[]
): EmployerMoneyGivenCardView => {
  const activeSettlement = resolveActiveSettlementForBudget(budget, settlements);
  if (activeSettlement?.status === 'submitted') {
    return {
      badge: 'pending_review',
      activeSettlement,
      detailPath: `/settlements/${activeSettlement.id}`,
      showSettlementResult: true,
    };
  }
  if (activeSettlement?.status === 'reviewed') {
    return {
      badge: 'settled',
      activeSettlement,
      detailPath: `/settlements/${activeSettlement.id}`,
      showSettlementResult: true,
    };
  }
  return {
    badge: 'unsettled',
    activeSettlement: null,
    detailPath: `/settlements/by-budget/${budget.id}`,
    showSettlementResult: false,
  };
};

export type GivenVsExpensesBar = {
  budgetId: string;
  periodLabel: string;
  givenAmount: number;
  expensesAmount: number | null;
  expensesTone: 'orange' | 'red' | null;
};

/** Chart X-axis: MM-DD – MM-DD (year omitted). */
const formatChartPeriodLabel = (startDate: string, endDate: string): string => {
  const start = startDate.slice(5, 10);
  const end = endDate.slice(5, 10);
  if (start === end) {
    return start;
  }
  return `${start} – ${end}`;
};

export const HELPER_RECEIVED_VS_EXPENSES_CHART_LIMIT = 4;

export const buildHelperReceivedVsExpensesBars = (
  mainBudgets: BudgetListItem[]
): GivenVsExpensesBar[] => {
  const mainsOnly = mainBudgets.filter(
    (budget) => budget.budgetKind === 'main' && budget.parentBudgetId === null
  );
  const sorted = [...mainsOnly].sort((a, b) => {
    if (a.startDate !== b.startDate) {
      return b.startDate.localeCompare(a.startDate);
    }
    return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
  });
  return sorted.slice(0, HELPER_RECEIVED_VS_EXPENSES_CHART_LIMIT).map((budget) => ({
    budgetId: budget.id,
    periodLabel: formatChartPeriodLabel(budget.startDate, budget.endDate),
    givenAmount: budget.amount,
    expensesAmount: budget.totalSpent,
    expensesTone: 'red' as const,
  }));
};

export const buildGivenVsExpensesBars = (
  mainBudgets: BudgetListItem[],
  settlements: SettlementListItem[]
): GivenVsExpensesBar[] => {
  const mainsOnly = mainBudgets.filter(
    (budget) => budget.budgetKind === 'main' && budget.parentBudgetId === null
  );
  const sorted = [...mainsOnly].sort((a, b) => {
    if (a.startDate !== b.startDate) {
      return b.startDate.localeCompare(a.startDate);
    }
    return b.endDate.localeCompare(a.endDate);
  });
  return sorted.slice(0, 4).map((budget) => {
    const activeSettlement = resolveActiveSettlementForBudget(budget, settlements);
    if (activeSettlement?.status === 'submitted') {
      return {
        budgetId: budget.id,
        periodLabel: formatChartPeriodLabel(budget.startDate, budget.endDate),
        givenAmount: budget.amount,
        expensesAmount: activeSettlement.totalSpent,
        expensesTone: 'orange',
      };
    }
    if (activeSettlement?.status === 'reviewed') {
      return {
        budgetId: budget.id,
        periodLabel: formatChartPeriodLabel(budget.startDate, budget.endDate),
        givenAmount: budget.amount,
        expensesAmount: activeSettlement.totalSpent,
        expensesTone: 'red',
      };
    }
    return {
      budgetId: budget.id,
      periodLabel: formatChartPeriodLabel(budget.startDate, budget.endDate),
      givenAmount: budget.amount,
      expensesAmount: null,
      expensesTone: null,
    };
  });
};

export const shouldHideBudgetEditBySettlement = (
  budgetStartDate: string,
  budgetEndDate: string,
  settlements: SettlementListItem[]
): boolean =>
  settlements.some(
    (settlementRow) =>
      (settlementRow.status === 'submitted' || settlementRow.status === 'reviewed') &&
      settlementRow.startDate <= budgetEndDate &&
      settlementRow.endDate >= budgetStartDate
  );

export const formatEmployerMoneyAmount = (amount: number): string =>
  amount < 0 ? `-$${Math.abs(amount).toFixed(2)}` : `$${amount.toFixed(2)}`;

/** Y-axis ceiling for the given-vs-expenses chart (e.g. 1369.6 → 1400, 69.6 → 70). */
export const computeGivenVsExpensesChartDisplayMax = (rawMax: number): number => {
  if (rawMax <= 0) {
    return 1;
  }
  if (rawMax >= 1000) {
    return Math.ceil(rawMax / 100) * 100;
  }
  return Math.ceil(rawMax / 10) * 10;
};

/** Y-axis midpoint label for the given-vs-expenses chart (e.g. 70 → 35, 370 → 185). */
export const computeGivenVsExpensesChartDisplayMid = (displayMax: number): number =>
  displayMax / 2;

export const formatChartAxisAmount = (amount: number): string => `$${Math.round(amount)}`;

/** Map a chart value to pixel height / bottom offset using the same scale as Y-axis labels. */
export const computeGivenVsExpensesChartValuePx = (
  value: number,
  displayMax: number,
  chartHeightPx: number
): number => {
  if (value <= 0 || displayMax <= 0 || chartHeightPx <= 0) {
    return 0;
  }
  const scaled = Math.round((value / displayMax) * chartHeightPx);
  return Math.min(chartHeightPx, Math.max(0, scaled));
};

export const computeGivenVsExpensesChartBarHeightPx = (
  amount: number,
  displayMax: number,
  chartHeightPx: number
): number => {
  const scaled = computeGivenVsExpensesChartValuePx(amount, displayMax, chartHeightPx);
  if (amount > 0 && scaled === 0) {
    return 4;
  }
  return scaled;
};

export const formatEmployerSettlementBalance = (balance: number): string => {
  const prefix = balance >= 0 ? '+' : '-';
  return `${prefix}${formatEmployerMoneyAmount(Math.abs(balance))}`;
};

export const resolveEmployerHistoryStatusLabel = (
  status: SettlementHistoryItem['status'],
  t: (key: string) => string
): string => {
  if (status === 'rejected') {
    return t('settlement.historyStatus.rejected');
  }
  if (status === 'withdrawn') {
    return t('settlement.historyStatus.withdrawn');
  }
  return t('settlement.historyStatus.unlocked');
};

export const resolveEmployerHistoryStatusClass = (
  status: SettlementHistoryItem['status']
): string => {
  if (status === 'withdrawn') {
    return 'text-green-800 dark:text-green-300';
  }
  if (status === 'rejected') {
    return 'text-red-800 dark:text-red-300';
  }
  return 'text-blue-800 dark:text-blue-300';
};

export const formatEmployerHistorySettlementResult = (
  balance: number,
  t: (key: string) => string
): string => {
  const kindLabel = balance >= 0 ? t('settlement.surplus') : t('settlement.overspent');
  return `${kindLabel} ${formatEmployerMoneyAmount(Math.abs(balance))}`;
};
